import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { motion } from 'motion/react';
import { Check, X } from 'lucide-react';
import { useMathEducation } from './MathEducationProvider';
import {
  CONTEXTS,
  RatioQuestion,
  computeAnswer,
  findContext,
  RatioContext,
  QUANTITY_COLORS,
} from './RatioQuestionsColored';
import { selectNextQuestion, recordCorrectAnswer, recordIncorrectAnswer } from './questionSelection';
import { generateAllQuestionsForContext } from './RatioQuestionsColored';
import { RichQuestionText } from './RichQuestionText';
import NumberInput from './NumberInput';

/**
 * Parse a ratio string in various formats (colon, fraction, word) into an array of numbers
 * Returns both the numbers and the detected format
 */
function parseRatioAnswer(input: string): { numbers: number[]; format: ':' | '/' | 'to' } | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Try colon format (e.g., "1:2", "1:2:3")
  if (trimmed.includes(':')) {
    const parts = trimmed.split(':').map(s => parseInt(s.trim()));
    if (parts.every(n => !isNaN(n) && n > 0)) {
      return { numbers: parts, format: ':' };
    }
  }

  // Try fraction format (e.g., "1/2")
  if (trimmed.includes('/')) {
    const parts = trimmed.split('/').map(s => parseInt(s.trim()));
    if (parts.length === 2 && parts.every(n => !isNaN(n) && n > 0)) {
      return { numbers: parts, format: '/' };
    }
  }

  // Try word format (e.g., "1 to 2", "1 to 2 to 3")
  const wordMatch = trimmed.match(/(\d+)\s*to\s*(\d+)(?:\s*to\s*(\d+))?/i);
  if (wordMatch) {
    const numbers = [
      parseInt(wordMatch[1]),
      parseInt(wordMatch[2]),
      wordMatch[3] ? parseInt(wordMatch[3]) : null,
    ].filter(n => n !== null) as number[];

    if (numbers.every(n => !isNaN(n) && n > 0)) {
      return { numbers, format: 'to' };
    }
  }

  return null;
}

/**
 * Check if two ratio answers match (numbers and format)
 */
function answersMatch(
  userAnswer: { numbers: number[]; format: ':' | '/' | 'to' } | null,
  correctAnswer: { numbers: number[]; format: ':' | '/' | 'to' } | null,
): boolean {
  if (!userAnswer || !correctAnswer) return false;

  // Format must match
  if (userAnswer.format !== correctAnswer.format) return false;

  // Must have same number of parts
  if (userAnswer.numbers.length !== correctAnswer.numbers.length) return false;

  // For 2-part ratios, check if they're exactly equal
  if (userAnswer.numbers.length === 2) {
    return userAnswer.numbers[0] === correctAnswer.numbers[0] && userAnswer.numbers[1] === correctAnswer.numbers[1];
  }

  // For 3+ part ratios, require exact match
  return correctAnswer.numbers.every((val, idx) => userAnswer.numbers[idx] === val);
}

/**
 * Check if numbers match regardless of format
 */
function numbersMatch(
  userAnswer: { numbers: number[]; format: ':' | '/' | 'to' } | null,
  correctAnswer: { numbers: number[]; format: ':' | '/' | 'to' } | null,
): boolean {
  if (!userAnswer || !correctAnswer) return false;

  if (userAnswer.numbers.length !== correctAnswer.numbers.length) return false;

  return correctAnswer.numbers.every((val, idx) => userAnswer.numbers[idx] === val);
}

/**
 * Get format name for display
 */
function getFormatName(format: ':' | '/' | 'to'): string {
  switch (format) {
    case ':':
      return 'colon notation (e.g., 2:3)';
    case '/':
      return 'fraction notation (e.g., 2/3)';
    case 'to':
      return 'word form (e.g., 2 to 3)';
  }
}

/**
 * Analyze user's answer and provide specific feedback
 */
function analyzeAnswer(
  userAnswer: { numbers: number[]; format: ':' | '/' | 'to' } | null,
  correctAnswer: { numbers: number[]; format: ':' | '/' | 'to' } | null,
  question: RatioQuestion,
  context: RatioContext,
): string {
  void question;
  if (!userAnswer) {
    return 'Your response was difficult for me to read.';
  }

  if (!correctAnswer) {
    return 'Take another look at the quantities described in the problem.';
  }

  // Check if numbers match but format is wrong
  if (numbersMatch(userAnswer, correctAnswer) && userAnswer.format !== correctAnswer.format) {
    return `Your numbers are correct! Now try expressing the answer using ${getFormatName(correctAnswer.format)}.`;
  }

  // Check if numbers are reversed
  if (userAnswer.numbers.length === 2 && correctAnswer.numbers.length === 2) {
    if (userAnswer.numbers[0] === correctAnswer.numbers[1] && userAnswer.numbers[1] === correctAnswer.numbers[0]) {
      return 'You had the right numbers, but in the wrong order!';
    }
  }

  // Check individual numbers
  const allNumbers = [...context.parts.map(p => p.count), ...(context.whole ? [context.whole.count] : [])];

  const firstCorrect = userAnswer.numbers[0] === correctAnswer.numbers[0];
  const secondCorrect = userAnswer.numbers[1] === correctAnswer.numbers[1];

  if (firstCorrect && !secondCorrect) {
    return 'Your first number is correct!';
  } else if (!firstCorrect && secondCorrect) {
    return 'Your second number is correct!';
  }

  // Check if numbers are from the context but applied incorrectly
  const userNumbersInContext = userAnswer.numbers.every(n => allNumbers.includes(n));
  if (userNumbersInContext) {
    return 'Your numbers are from the problem, but not in the right relationship.';
  }

  return 'Take another look at the quantities described in the problem.';
}

/**
 * Generate specific hint for "Not quite right" message
 */
function getSpecificHint(
  userAnswer: { numbers: number[]; format: ':' | '/' | 'to' } | null,
  correctAnswer: { numbers: number[]; format: ':' | '/' | 'to' } | null,
  question: RatioQuestion,
  context: RatioContext,
): string {
  void question;
  void context;
  if (!userAnswer || !correctAnswer) {
    return 'Check that your answer is in the correct format.';
  }

  // Priority 1: Check format if numbers are correct
  if (numbersMatch(userAnswer, correctAnswer) && userAnswer.format !== correctAnswer.format) {
    return `Use ${getFormatName(correctAnswer.format)}.`;
  }

  // Priority 2: Check if numbers are correct
  const firstCorrect = userAnswer.numbers[0] === correctAnswer.numbers[0];
  const secondCorrect = userAnswer.numbers[1] === correctAnswer.numbers[1];

  if (!firstCorrect && !secondCorrect) {
    return 'Check your numbers.';
  }

  if (!firstCorrect) {
    return 'Check your first number.';
  }

  if (!secondCorrect) {
    return 'Check your second number.';
  }

  // Priority 3: Check order
  if (userAnswer.numbers[0] === correctAnswer.numbers[1] && userAnswer.numbers[1] === correctAnswer.numbers[0]) {
    return 'Check the order of your numbers.';
  }

  return 'Check your answer carefully.';
}

/**
 * Get all valid numbers from a context (all part counts and whole count if exists)
 */
function getValidNumbersFromContext(context: RatioContext): number[] {
  const numbers = context.parts.map(p => p.count);
  if (context.whole) {
    numbers.push(context.whole.count);
  }
  return [...new Set(numbers)].sort((a, b) => a - b);
}

/**
 * Simple block visualization component
 */
function BlockVisualization({ quantities }: { quantities: { count: number; label: string; color: string }[] }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-4)',
        padding: 'var(--spacing-4)',
        background: 'var(--muted)',
        borderRadius: 'var(--radius)',
      }}
    >
      {quantities.map((qty, idx) => (
        <div key={idx}>
          <div
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--muted-foreground)',
              fontFamily: 'var(--font-atkinson)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {qty.label}: {qty.count}
          </div>
          <div style={{ display: 'flex', gap: 'var(--spacing-1)', flexWrap: 'wrap' }}>
            {Array.from({ length: Math.min(qty.count, 100) }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: '32px',
                  height: '32px',
                  background: qty.color,
                  borderRadius: 'var(--radius)',
                  border: '2px solid var(--border)',
                }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdvancedModeRatioNew() {
  const { progress, recordAdvancedModeAnswer, updateQuestionHistory } = useMathEducation();
  const [currentQuestion, setCurrentQuestion] = useState<RatioQuestion | null>(null);
  const [userInput, setUserInput] = useState<string>('');
  const [showFeedback, setShowFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [attemptCountForCurrentQuestion, setAttemptCountForCurrentQuestion] = useState(0);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [userAnswerParsed, setUserAnswerParsed] = useState<{ numbers: number[]; format: ':' | '/' | 'to' } | null>(
    null,
  );

  // Structured input state (after first incorrect attempt)
  const [useStructuredInput, setUseStructuredInput] = useState(false);
  const [structuredNum1, setStructuredNum1] = useState<string>('');
  const [structuredNum2, setStructuredNum2] = useState<string>('');
  const [structuredFormat, setStructuredFormat] = useState<':' | '/' | 'to'>(':');
  const [showDropdowns, setShowDropdowns] = useState(false);
  const [structuredAttemptCount, setStructuredAttemptCount] = useState(0);

  // Generate all questions once
  const allQuestions = useMemo(() => CONTEXTS.flatMap(generateAllQuestionsForContext), []);

  // Load initial question
  useEffect(() => {
    if (!currentQuestion && allQuestions.length > 0) {
      const firstQuestion = selectNextQuestion(allQuestions, CONTEXTS, progress.advancedModeQuestionHistory);
      setCurrentQuestion(firstQuestion);
    }
  }, [currentQuestion, allQuestions, progress.advancedModeQuestionHistory]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(e.target.value);
  }, []);

  const handleStructuredInputChange = useCallback(() => {
    // Once dropdowns are shown, don't switch back
    if (showDropdowns) return;

    // Dropdowns only activate after the second structured attempt check
    if (!currentQuestion || structuredAttemptCount < 2) {
      return;
    }

    const context = findContext(currentQuestion.contextId);
    const validNumbers = getValidNumbersFromContext(context);

    const num1 = parseInt(structuredNum1);
    const num2 = parseInt(structuredNum2);

    // Check if numbers are valid for this context
    const num1Valid = !isNaN(num1) && validNumbers.includes(num1);
    const num2Valid = !isNaN(num2) && validNumbers.includes(num2);

    // If either number is out of scope, permanently switch to dropdowns
    if (!num1Valid || !num2Valid) {
      setShowDropdowns(true);
    }
  }, [structuredNum1, structuredNum2, currentQuestion, structuredAttemptCount, showDropdowns]);

  useEffect(() => {
    if (useStructuredInput) {
      handleStructuredInputChange();
    }
  }, [structuredNum1, structuredNum2, useStructuredInput, handleStructuredInputChange]);

  const checkAnswer = useCallback(() => {
    if (!currentQuestion) return;

    let parsedAnswer: { numbers: number[]; format: ':' | '/' | 'to' } | null = null;

    if (useStructuredInput) {
      // Build answer from structured input
      const num1 = parseInt(structuredNum1);
      const num2 = parseInt(structuredNum2);

      if (!isNaN(num1) && !isNaN(num2) && num1 > 0 && num2 > 0) {
        parsedAnswer = { numbers: [num1, num2], format: structuredFormat };
      }
    } else {
      // Parse text input
      parsedAnswer = parseRatioAnswer(userInput);
    }

    setUserAnswerParsed(parsedAnswer);

    // Get correct answer and parse it
    const correctAnswerString = computeAnswer(currentQuestion);
    const correctAnswer = parseRatioAnswer(correctAnswerString);

    if (!correctAnswer) {
      console.error('Failed to parse correct answer:', correctAnswerString);
      return;
    }

    const isCorrect = parsedAnswer ? answersMatch(parsedAnswer, correctAnswer) : false;
    const isFirstTry = attemptCountForCurrentQuestion === 0;

    if (isCorrect) {
      setShowFeedback('correct');
      setFeedbackMessage('');

      // Record answer and update history
      recordAdvancedModeAnswer(true, isFirstTry);

      const context = findContext(currentQuestion.contextId);
      const updatedHistory = recordCorrectAnswer(
        progress.advancedModeQuestionHistory,
        currentQuestion,
        context.category,
        isFirstTry,
      );
      updateQuestionHistory(updatedHistory);

      // Move to next question after a delay
      setTimeout(() => {
        const nextQuestion = selectNextQuestion(allQuestions, CONTEXTS, updatedHistory);
        setCurrentQuestion(nextQuestion);
        setUserInput('');
        setShowFeedback(null);
        setAttemptCountForCurrentQuestion(0);
        setFeedbackMessage('');
        setUserAnswerParsed(null);
        setUseStructuredInput(false);
        setStructuredNum1('');
        setStructuredNum2('');
        setStructuredFormat(':');
        setShowDropdowns(false);
        setStructuredAttemptCount(0);
      }, 1500);
    } else {
      setShowFeedback('incorrect');
      setAttemptCountForCurrentQuestion(prev => prev + 1);

      // Analyze answer and generate feedback
      const context = findContext(currentQuestion.contextId);
      const feedback = analyzeAnswer(parsedAnswer, correctAnswer, currentQuestion, context);
      setFeedbackMessage(feedback);

      // Switch to structured input after first attempt
      if (!useStructuredInput) {
        setUseStructuredInput(true);

        // Pre-fill if we have a parsed answer - use user's format and numbers
        if (parsedAnswer && parsedAnswer.numbers.length === 2) {
          setStructuredFormat(parsedAnswer.format);
          setStructuredNum1(parsedAnswer.numbers[0].toString());
          setStructuredNum2(parsedAnswer.numbers[1].toString());
        } else {
          // Default to correct format if we couldn't parse user's answer
          setStructuredFormat(correctAnswer.format);
        }
      }

      // Record incorrect answer and update history
      recordAdvancedModeAnswer(false, isFirstTry);

      const updatedHistory = recordIncorrectAnswer(progress.advancedModeQuestionHistory, currentQuestion);
      updateQuestionHistory(updatedHistory);

      // Increment structured attempt count
      setStructuredAttemptCount(prev => prev + 1);
    }
  }, [
    currentQuestion,
    userInput,
    attemptCountForCurrentQuestion,
    allQuestions,
    progress.advancedModeQuestionHistory,
    recordAdvancedModeAnswer,
    updateQuestionHistory,
    useStructuredInput,
    structuredNum1,
    structuredNum2,
    structuredFormat,
  ]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && userInput.trim() && showFeedback !== 'correct') {
        checkAnswer();
      }
    },
    [userInput, showFeedback, checkAnswer],
  );

  if (!currentQuestion) {
    return (
      <div className="tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center">
        <div
          className="tw-rounded"
          style={{
            background: 'var(--card)',
            borderRadius: 'var(--radius-card)',
            border: '1px solid var(--border)',
            padding: 'var(--spacing-8)',
            boxShadow: 'var(--elevation-lg)',
          }}
        >
          <p style={{ color: 'var(--foreground)', fontFamily: 'var(--font-atkinson)' }}>Loading questions...</p>
        </div>
      </div>
    );
  }

  const context = findContext(currentQuestion.contextId);
  const isAnswerComplete = useStructuredInput
    ? structuredNum1.trim() !== '' && structuredNum2.trim() !== ''
    : userInput.trim() !== '';
  const validNumbers = getValidNumbersFromContext(context);

  // Compute correct answer for this question
  const correctAnswerString = computeAnswer(currentQuestion);
  const correctAnswer = parseRatioAnswer(correctAnswerString);

  // Generate block visualization data based on user's answer
  let blockVisualizationData: { count: number; label: string; color: string }[] = [];
  if (userAnswerParsed && userAnswerParsed.numbers.length === 2) {
    // Get the actual parts referenced in the question
    // IMPORTANT: Respect the reverse property to match the question order
    const askedFirstKey = currentQuestion.reverse ? currentQuestion.denominator : currentQuestion.numerator;
    const askedSecondKey = currentQuestion.reverse ? currentQuestion.numerator : currentQuestion.denominator;

    const firstPart = context.parts.find(p => p.key === askedFirstKey) || context.whole;
    const secondPart = context.parts.find(p => p.key === askedSecondKey) || context.whole;

    // Determine labels based on which parts the question is asking about (in the order asked)
    let label1 = 'First quantity';
    let label2 = 'Second quantity';

    if (firstPart) {
      label1 = userAnswerParsed.numbers[0] === 1 ? firstPart.singular : firstPart.plural;
    }

    if (secondPart) {
      label2 = userAnswerParsed.numbers[1] === 1 ? secondPart.singular : secondPart.plural;
    }

    // Use the actual colors from the question's parts to match the problem text
    const color1 = firstPart?.colorIndex !== undefined ? QUANTITY_COLORS[firstPart.colorIndex] : QUANTITY_COLORS[0];
    const color2 = secondPart?.colorIndex !== undefined ? QUANTITY_COLORS[secondPart.colorIndex] : QUANTITY_COLORS[1];

    blockVisualizationData = [
      { count: userAnswerParsed.numbers[0], label: label1, color: color1 },
      { count: userAnswerParsed.numbers[1], label: label2, color: color2 },
    ];
  }

  return (
    <div
      className="tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center"
      style={{ padding: 'var(--spacing-4)' }}
    >
      <motion.div
        key={currentQuestion.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="tw-flex tw-w-full tw-max-w-2xl tw-min-h-0 tw-flex-col"
        style={{
          background: 'var(--card)',
          borderRadius: 'var(--radius-card)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--elevation-lg)',
          maxHeight: '100%',
        }}
      >
        <div
          className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto"
          style={{ padding: 'var(--spacing-8)' }}
        >
          {/* Problem Prompt */}
          <div style={{ marginBottom: 'var(--spacing-8)' }}>
            <h2
              style={{
                color: 'var(--foreground)',
                fontFamily: 'var(--font-atkinson)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              Question {progress.advancedModeQuestionsCorrect + 1}
            </h2>
            <div
              style={{
                color: 'var(--muted-foreground)',
                fontFamily: 'var(--font-atkinson)',
                lineHeight: '1.5',
              }}
            >
              <RichQuestionText question={currentQuestion} />
            </div>
          </div>

          {/* Answer Input Area */}
          <div style={{ marginBottom: 'var(--spacing-6)' }}>
            <label
              className="tw-block"
              style={{
                color: 'var(--foreground)',
                fontFamily: 'var(--font-atkinson)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              Your answer:
            </label>

            {!useStructuredInput ? (
              <input
                type="text"
                value={userInput}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter ratio"
                className="tw-flex tw-h-10 tw-w-full tw-rounded-md tw-border tw-border-border tw-bg-input-background tw-text-foreground tw-px-3 tw-py-2 placeholder:tw-text-muted-foreground focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                style={{
                  width: '100%',
                  fontFamily: 'var(--font-atkinson)',
                  fontSize: 'var(--text-lg)',
                  borderRadius: 'var(--radius)',
                  borderColor: 'var(--border)',
                }}
              />
            ) : (
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
                {showDropdowns ? (
                  <select
                    value={structuredNum1}
                    onChange={e => setStructuredNum1(e.target.value)}
                    className="tw-flex tw-h-10 tw-rounded-md tw-border tw-border-border tw-bg-input-background tw-text-foreground tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2"
                    style={{
                      flex: 1,
                      fontFamily: 'var(--font-atkinson)',
                      fontSize: 'var(--text-lg)',
                      borderRadius: 'var(--radius)',
                      borderColor: 'var(--border)',
                    }}
                  >
                    <option value="">Select</option>
                    {validNumbers.map(num => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={{ flex: 1 }}>
                    <NumberInput
                      value={structuredNum1}
                      onChange={e => setStructuredNum1(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                )}

                <select
                  value={structuredFormat}
                  onChange={e => setStructuredFormat(e.target.value as ':' | '/' | 'to')}
                  className="tw-flex tw-h-10 tw-rounded-md tw-border tw-border-border tw-bg-input-background tw-text-foreground tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2"
                  style={{
                    fontFamily: 'var(--font-atkinson)',
                    fontSize: 'var(--text-lg)',
                    borderRadius: 'var(--radius)',
                    borderColor: 'var(--border)',
                  }}
                >
                  <option value=":">:</option>
                  <option value="/">/</option>
                  <option value="to">to</option>
                </select>

                {showDropdowns ? (
                  <select
                    value={structuredNum2}
                    onChange={e => setStructuredNum2(e.target.value)}
                    className="tw-flex tw-h-10 tw-rounded-md tw-border tw-border-border tw-bg-input-background tw-text-foreground tw-px-3 tw-py-2 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2"
                    style={{
                      flex: 1,
                      fontFamily: 'var(--font-atkinson)',
                      fontSize: 'var(--text-lg)',
                      borderRadius: 'var(--radius)',
                      borderColor: 'var(--border)',
                    }}
                  >
                    <option value="">Select</option>
                    {validNumbers.map(num => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={{ flex: 1 }}>
                    <NumberInput
                      value={structuredNum2}
                      onChange={e => setStructuredNum2(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Check Answer Button */}
          <button
            onClick={checkAnswer}
            disabled={!isAnswerComplete || showFeedback === 'correct'}
            className="tw-w-full tw-rounded tw-transition-all"
            style={{
              padding: 'var(--spacing-3)',
              background: isAnswerComplete && showFeedback !== 'correct' ? 'var(--primary)' : 'var(--muted)',
              color:
                isAnswerComplete && showFeedback !== 'correct'
                  ? 'var(--primary-foreground)'
                  : 'var(--muted-foreground)',
              borderRadius: 'var(--radius)',
              fontFamily: 'var(--font-atkinson)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-weight-bold)',
              opacity: !isAnswerComplete || showFeedback === 'correct' ? 0.5 : 1,
              cursor: !isAnswerComplete || showFeedback === 'correct' ? 'not-allowed' : 'pointer',
              marginBottom: 'var(--spacing-6)',
            }}
          >
            Check Answer
          </button>

          {/* Partial correctness feedback */}
          {showFeedback === 'incorrect' && feedbackMessage && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="tw-rounded"
              style={{
                marginBottom: 'var(--spacing-6)',
                padding: 'var(--spacing-4)',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
              }}
            >
              <p
                style={{
                  color: 'rgb(21, 128, 61)',
                  fontSize: 'var(--text-base)',
                  fontFamily: 'var(--font-atkinson)',
                }}
              >
                {feedbackMessage}
              </p>
            </motion.div>
          )}

          {/* Yellow feedback with specific hint */}
          {showFeedback === 'incorrect' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="tw-rounded tw-flex tw-items-center"
              style={{
                marginBottom: 'var(--spacing-6)',
                padding: 'var(--spacing-4)',
                gap: 'var(--spacing-3)',
                background: 'rgba(234, 179, 8, 0.1)',
                border: `2px solid rgba(234, 179, 8, 0.5)`,
              }}
            >
              <div
                className="tw-rounded-full tw-flex tw-items-center tw-justify-center"
                style={{
                  width: '24px',
                  height: '24px',
                  background: 'rgb(234, 179, 8)',
                }}
              >
                <X style={{ width: '16px', height: '16px', color: 'white' }} />
              </div>
              <span
                style={{
                  color: 'rgb(161, 98, 7)',
                  fontFamily: 'var(--font-atkinson)',
                }}
              >
                Not quite right. {getSpecificHint(userAnswerParsed, correctAnswer, currentQuestion, context)} Try again!
              </span>
            </motion.div>
          )}

          {/* Block visualization */}
          {showFeedback === 'incorrect' && userAnswerParsed && blockVisualizationData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              style={{ marginBottom: 'var(--spacing-6)' }}
            >
              <div
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--muted-foreground)',
                  fontFamily: 'var(--font-atkinson)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                Your answer represents:
              </div>
              <BlockVisualization quantities={blockVisualizationData} />
            </motion.div>
          )}

          {/* General hint for incorrect answers */}
          {showFeedback === 'incorrect' && attemptCountForCurrentQuestion > 1 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="tw-rounded"
              style={{
                marginBottom: 'var(--spacing-6)',
                padding: 'var(--spacing-4)',
                background: 'var(--muted)',
                border: '1px solid var(--border)',
              }}
            >
              <p
                style={{
                  color: 'var(--muted-foreground)',
                  fontSize: 'var(--text-base)',
                  fontFamily: 'var(--font-atkinson)',
                }}
              >
                💡 Check your work. Count the parts described in the context carefully, and make sure you&apos;re
                expressing the ratio in the correct format.
              </p>
            </motion.div>
          )}

          {/* Correct answer feedback */}
          {showFeedback === 'correct' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="tw-rounded tw-flex tw-items-center"
              style={{
                marginBottom: 'var(--spacing-6)',
                padding: 'var(--spacing-4)',
                gap: 'var(--spacing-3)',
                background: 'rgba(34, 197, 94, 0.1)',
                border: `2px solid rgba(34, 197, 94, 0.5)`,
              }}
            >
              <div
                className="tw-rounded-full tw-flex tw-items-center tw-justify-center"
                style={{
                  width: '24px',
                  height: '24px',
                  background: 'rgb(34, 197, 94)',
                }}
              >
                <Check style={{ width: '16px', height: '16px', color: 'white' }} />
              </div>
              <span
                style={{
                  color: 'rgb(34, 197, 94)',
                  fontFamily: 'var(--font-atkinson)',
                }}
              >
                Correct! {attemptCountForCurrentQuestion === 0 ? 'Great work on the first try!' : 'Nice job!'}
              </span>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
